import React, { useMemo, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IPerson {
  id: string;
  name: string;
  role: string;
  team: string;
  joined: string;
  status: "active" | "invited" | "archived";
}

const PEOPLE: IPerson[] = [
  { id: "1", name: "Aylin Demir", role: "Engineering Lead", team: "Frontend", joined: "2024-01-10", status: "active" },
  { id: "2", name: "Berkay Yıldız", role: "Senior Engineer", team: "Backend", joined: "2024-03-22", status: "active" },
  { id: "3", name: "Ceren Kaya", role: "Product Designer", team: "Design", joined: "2024-05-04", status: "invited" },
  { id: "4", name: "Doruk Aksoy", role: "QA Engineer", team: "QA", joined: "2023-11-19", status: "active" },
  { id: "5", name: "Elif Mert", role: "Engineering Manager", team: "Frontend", joined: "2023-08-01", status: "active" },
  { id: "6", name: "Furkan Ceylan", role: "Senior Engineer", team: "Frontend", joined: "2024-02-14", status: "active" },
  { id: "7", name: "Gül Atalay", role: "Researcher", team: "Design", joined: "2024-06-30", status: "archived" }
];

interface IStatefulProps {
  enableFilters?: boolean;
  enableSelection?: boolean;
  pageSize?: number;
}

const Stateful: React.FC<IStatefulProps> = ({ enableFilters, enableSelection, pageSize }) => {
  const columns: KuikaTableColumn<IPerson>[] = useMemo(
    (): KuikaTableColumn<IPerson>[] => [
      {
        id: "name",
        header: () => "Name",
        cell: (row: IPerson) => row.name,
        accessorKey: "name",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "text"
      },
      { id: "role", header: () => "Role", cell: (row: IPerson) => row.role, accessorKey: "role", enableSorting: true },
      {
        id: "team",
        header: () => "Team",
        cell: (row: IPerson) => row.team,
        accessorKey: "team",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect"
      },
      {
        id: "joined",
        header: () => "Joined",
        cell: (row: IPerson) => row.joined,
        accessorKey: "joined",
        enableSorting: true,
        align: "right"
      },
      {
        id: "status",
        header: () => "Status",
        cell: (row: IPerson) => row.status,
        accessorKey: "status",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect"
      }
    ],
    []
  );

  return (
    <KuikaTable<IPerson>
      data={PEOPLE}
      columns={columns}
      rowKey={(row: IPerson) => row.id}
      enableSorting
      enableColumnFilters={enableFilters}
      enableRowSelection={enableSelection}
      pageSize={pageSize}
    />
  );
};

const REVEAL_COLUMNS: KuikaTableColumn<IPerson>[] = [
  { id: "name", header: (): string => "Name", cell: (row: IPerson): string => row.name, accessorKey: "name" },
  { id: "team", header: (): string => "Team", cell: (row: IPerson): string => row.team, accessorKey: "team" }
];

interface IRevealDemoProps {
  pageSize?: number;
}

/** revealRowKey: the row a save landed on lights up (and, paged, the table turns to its page). */
const RevealDemo: React.FC<IRevealDemoProps> = ({ pageSize }: IRevealDemoProps): JSX.Element => {
  const [revealRowKey, setRevealRowKey] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number>(PEOPLE.length - 1);

  const revealNext = (): void => {
    const next: number = (cursor + 1) % PEOPLE.length;
    setCursor(next);
    setRevealRowKey(PEOPLE[next].id);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <div>
        <KuikaButton type="secondary" size="sm" onClick={revealNext}>
          Reveal {PEOPLE[(cursor + 1) % PEOPLE.length].name}
        </KuikaButton>
      </div>
      <KuikaTable<IPerson>
        data={PEOPLE}
        columns={REVEAL_COLUMNS}
        rowKey={(row: IPerson): string => row.id}
        enablePagination={pageSize !== undefined}
        pageSize={pageSize}
        fitContentHeight
        revealRowKey={revealRowKey}
        onRowRevealed={(): void => setRevealRowKey(null)}
      />
    </div>
  );
};

export const TableStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Table"
      componentName="KuikaTable"
      sourcePath="ui-kit/kuika-table"
      description="TanStack-backed data table with sorting, filters, column visibility, selection and pagination. Columns declare interactions via the column object."
    >
      <SpecRow label="Default" caption="sorting only" align="stretch">
        <div style={{ width: "100%" }}>
          <Stateful />
        </div>
      </SpecRow>
      <SpecRow label="With header filters" caption="text + multiselect filters per column" align="stretch">
        <div style={{ width: "100%" }}>
          <Stateful enableFilters />
        </div>
      </SpecRow>
      <SpecRow label="With row selection" align="stretch">
        <div style={{ width: "100%" }}>
          <Stateful enableSelection />
        </div>
      </SpecRow>
      <SpecRow label="With pagination" caption="pageSize=3" align="stretch">
        <div style={{ width: "100%" }}>
          <Stateful pageSize={3} />
        </div>
      </SpecRow>
      <SpecRow
        label="Reveal a row"
        caption="revealRowKey without pages — the row scrolls into view and fades"
        align="stretch"
      >
        <RevealDemo />
      </SpecRow>
      <SpecRow
        label="Reveal on another page"
        caption="revealRowKey with pageSize=3 — turns to the row's page first"
        align="stretch"
      >
        <RevealDemo pageSize={3} />
      </SpecRow>
    </ShowcaseFrame>
  );
};
